/**
 * Notes step for intake forms (Clinical and CoCM). Composes the scribe
 * prefill runner, the scribe status UI, and the clinical notes fields.
 * The general scratch pad lives on the call-guide (Topics) step; here each
 * section surfaces its call-guide scratch read-only via a hover/copy icon.
 *
 * @agentic connections
 */

import { Alert, Text, Textarea } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import type { UseFormReturnType } from "@mantine/form";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type { Encounter, Patient, Practitioner } from "@medplum/fhirtypes";

import { FormField } from "@benchmark/ui/components/atomic";
import { QUESTIONNAIRE_URLS } from "@benchmark/core/constants";
import type { ScribeFormShape } from "@benchmark/core/types";

import { ClinicalNotesFields } from "../Encounter.clinicalNotesFields";
import { ScribePrefillRunner } from "../Encounter.scribePrefill";
import { ScribePrefillStatusUI } from "../Encounter.scribePrefillStatus";
import type { UseScribePipelineResult } from "../hooks/useScribePipeline";
import { isUnderEighteen } from "../note-sections";

/**
 * Form shapes that include scribe + clinical notes. The intake forms'
 * data types both extend `ScribeFormShape`, so the generic constraint
 * lets `ClinicalNotesStep` accept any future scribe-capable form
 * without `as unknown as` bridges.
 *
 * @agentic connections
 */
type ClinicalNotesCapableFormData = ScribeFormShape & {
  "encounter-type"?: string;
};

/**
 * Props for {@link ClinicalNotesStep}.
 *
 * @agentic connections
 */
export type ClinicalNotesStepProps<T extends ClinicalNotesCapableFormData> = {
  form: UseFormReturnType<T>;
  patient: Patient;
  scribe: UseScribePipelineResult;
  existingEncounter?: Encounter;
  /** When true, also render the General Note textarea (not used by intake forms by default). */
  showGeneralNote?: boolean;
};

/**
 * Whether to prompt the clinician to complete a Safety Plan (R1). Prompt only
 * when risk is actually indicated — never a hard block — and clear once the
 * Safety Plan is selected. The trigger is the structured AI signal
 * (`aiSafetyRiskPresent`), not any non-empty `safety` prose: denials and
 * non-safety distress used to trip it. In manual-override mode there is no AI
 * signal, so clinician-authored `safety` text is the trigger instead.
 *
 * @agentic connections
 */
export const shouldPromptForSafetyPlan = ({
  aiSafetyRiskPresent,
  safetyText,
  manualOverride,
  selectedQuestionnaires,
}: {
  aiSafetyRiskPresent?: boolean;
  safetyText?: unknown;
  manualOverride: boolean;
  selectedQuestionnaires: string[];
}): boolean => {
  const aiRiskPresent = aiSafetyRiskPresent === true;
  const clinicianAuthoredRisk =
    manualOverride &&
    typeof safetyText === "string" &&
    safetyText.trim().length > 0;
  return (
    (aiRiskPresent || clinicianAuthoredRisk) &&
    !selectedQuestionnaires.includes(QUESTIONNAIRE_URLS.SAFETY_PLAN)
  );
};

/**
 * Composed notes step: scribe runner + status UI + clinical impressions
 * textareas.
 *
 * @agentic connections
 */
export const ClinicalNotesStep = <T extends ClinicalNotesCapableFormData>({
  form,
  patient,
  scribe,
  existingEncounter,
  showGeneralNote = false,
}: ClinicalNotesStepProps<T>) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;

  // Pediatric subsections render only for patients under 18 (D10). Age is
  // computed against the encounter start when available, else today. Unknown
  // birth date is treated as adult (conservative).
  const referenceDate =
    existingEncounter?.period?.start ?? form.values.period?.start;
  const isPediatric = isUnderEighteen(patient.birthDate, referenceDate);

  // On an in-progress draft the clinician is still authoring, so retired-generic
  // fields that carry content (e.g. from a pre-split draft) must be editable —
  // read-only "(historical)" treatment is only correct for finalized encounters.
  const isDraft = existingEncounter?.status === "in-progress";

  // Safety Plan risk prompt (D2/D17/R1). See {@link shouldPromptForSafetyPlan}.
  const shouldPromptSafetyPlan = shouldPromptForSafetyPlan({
    aiSafetyRiskPresent: form.values._aiSafetyRiskPresent,
    safetyText: (form.values as Record<string, unknown>).safety,
    manualOverride: scribe.manualOverride,
    selectedQuestionnaires: ((form.values as Record<string, unknown>)
      .selectedQuestionnaires ?? []) as string[],
  });

  return (
    <>
      {shouldPromptSafetyPlan && (
        <Alert
          color="red"
          radius="md"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          mb="md"
          data-testid="safety-plan-prompt"
        >
          <Text fw={600} size="sm" mb={4}>
            Risk documented — complete a Safety Plan
          </Text>
          <Text size="sm">
            The Safety subsection records self-harm or suicidal-ideation
            history. Add the Safety Plan from the Assessments step and complete
            it with the patient.
          </Text>
        </Alert>
      )}
      {!scribe.manualOverride && (
        <ScribePrefillRunner
          key={scribe.scribeRunKey}
          shouldStart={
            !form.values._scribeApplied || !form.values.period?.start
          }
          medplum={medplum}
          patient={patient}
          profile={profile}
          form={form}
          applyScribePrefill={scribe.applyScribePrefill}
          setScribeLoading={scribe.setScribeLoading}
          currentEncounterId={existingEncounter?.id}
          onStatusChange={scribe.setScribeStatus}
          runKey={scribe.scribeRunKey}
          onCompositionsFound={scribe.setAvailableCompositions}
          selectedCompositionId={scribe.selectedCompositionId}
        />
      )}

      <ScribePrefillStatusUI
        scribeLoading={scribe.scribeLoading}
        scribeApplied={form.values._scribeApplied || false}
        scribeCompositionId={form.values._scribeCompositionId}
        scribeStatus={scribe.scribeStatus}
        manualOverride={scribe.manualOverride}
        isIntakeEncounter={true}
        onManualOverride={scribe.handleManualOverride}
        onRetryPrefill={scribe.handleRetryPrefill}
        availableCompositions={scribe.availableCompositions}
        selectedCompositionId={scribe.selectedCompositionId}
        onCompositionSelect={scribe.setSelectedCompositionId}
      />

      {showGeneralNote && (
        <FormField
          label="General Note"
          variant="section"
          showDivider={false}
          description="Document the key points of this interaction."
          required
          error={form.errors["comment"] as string}
        >
          <Textarea
            error={form.errors["comment"] ? <></> : undefined}
            value={form.values.comment ?? ""}
            onChange={(e) =>
              form.setFieldValue("comment" as never, e.target.value as never)
            }
            autosize
            minRows={3}
            placeholder="Document the key points of this interaction."
          />
        </FormField>
      )}

      <ClinicalNotesFields
        encounterType={form.values["encounter-type"] ?? ""}
        values={form.values as unknown as Record<string, string>}
        onChange={(key, value) =>
          form.setFieldValue(key as never, value as never)
        }
        disabled={!form.values._scribeApplied && !scribe.manualOverride}
        isPediatric={isPediatric}
        sectionScratch={form.values._sectionScratch ?? {}}
        legacyEditable={isDraft}
      />
    </>
  );
};
